import { Injectable, OnDestroy } from '@angular/core';
import { trackAnalyticsEvent } from '../shared/analytics';
import { LazyGlitchTipService } from './lazy-glitchtip.service';

export type UploadCheckpointStage =
  | 'upload_task_started'
  | 'r2_prepare_started'
  | 'r2_put_started'
  | 'r2_finalize_started';

type UploadCheckpointProvider = 'wordpress' | 'r2' | 'unknown';

type UploadResourceMetrics = {
  encodedSizeBytes?: number;
  fileSizeBytes?: number;
  heapUsedBytes?: number;
  heapTotalBytes?: number;
  heapLimitBytes?: number;
  deviceMemoryGb?: number;
};

type UploadCheckpoint = UploadResourceMetrics & {
  attemptId: string;
  ownerId?: string;
  stage: UploadCheckpointStage;
  provider: UploadCheckpointProvider;
  startedAt: number;
  updatedAt: number;
};

type UploadCheckpointRegistry = {
  version: 1;
  attempts: Record<string, UploadCheckpoint>;
};

type StartUploadCheckpoint = Pick<UploadResourceMetrics, 'encodedSizeBytes'>;
type AdvanceUploadCheckpoint = Pick<UploadResourceMetrics, 'fileSizeBytes'> & {
  provider?: Exclude<UploadCheckpointProvider, 'unknown'>;
};

type PerformanceWithMemory = Performance & {
  memory?: {
    usedJSHeapSize?: number;
    totalJSHeapSize?: number;
    jsHeapSizeLimit?: number;
  };
};

type NavigatorWithDeviceMemory = Navigator & { deviceMemory?: number };

type UploadCheckpointChannelMessage =
  | { type: 'probe'; requestId: string }
  | { type: 'active_attempts'; requestId: string; attemptIds: string[] };

const STORAGE_KEY = 'reportan:image-upload-checkpoints:v1';
const CHANNEL_NAME = 'reportan:image-upload-checkpoints';
const LIVE_PROBE_DURATION_MS = 50;

@Injectable({ providedIn: 'root' })
export class UploadCheckpointService implements OnDestroy {
  private readonly ownerId = crypto.randomUUID();
  private readonly channel = typeof BroadcastChannel === 'undefined'
    ? undefined
    : new BroadcastChannel(CHANNEL_NAME);

  constructor(private glitchTip: LazyGlitchTipService) {
    this.channel?.addEventListener('message', this.handleChannelMessage);
  }

  ngOnDestroy(): void {
    this.channel?.removeEventListener('message', this.handleChannelMessage);
    this.channel?.close();
  }

  start(input: StartUploadCheckpoint): string {
    const attemptId = crypto.randomUUID();
    const now = Date.now();
    const checkpoint: UploadCheckpoint = {
      attemptId,
      ownerId: this.ownerId,
      stage: 'upload_task_started',
      provider: 'unknown',
      startedAt: now,
      updatedAt: now,
      ...input,
      ...this.collectResourceMetrics(),
    };
    const attempts = this.loadAttempts();
    attempts.set(attemptId, checkpoint);
    this.saveAttempts(attempts);
    this.addStageBreadcrumb(checkpoint);
    return attemptId;
  }

  advance(
    attemptId: string,
    stage: UploadCheckpointStage,
    input: AdvanceUploadCheckpoint = {},
  ): void {
    const attempts = this.loadAttempts();
    const current = attempts.get(attemptId);
    if (!current) return;

    const checkpoint: UploadCheckpoint = {
      ...current,
      ...input,
      ...this.collectResourceMetrics(),
      stage,
      updatedAt: Date.now(),
    };
    attempts.delete(attemptId);
    attempts.set(attemptId, checkpoint);
    this.saveAttempts(attempts);
    this.addStageBreadcrumb(checkpoint);
  }

  finish(attemptId: string, _outcome: 'success' | 'failure'): void {
    const attempts = this.loadAttempts();
    if (!attempts.delete(attemptId)) return;
    this.saveAttempts(attempts);
  }

  async reportInterruptedUploads(): Promise<void> {
    const initialAttempts = this.loadAttempts();
    if (initialAttempts.size === 0) return;

    const liveAttemptIds = await this.findLiveAttemptIds(initialAttempts);
    const currentAttempts = this.loadAttempts();
    const attempts = [...initialAttempts.keys()]
      .filter((attemptId) => !liveAttemptIds.has(attemptId))
      .map((attemptId) => currentAttempts.get(attemptId))
      .filter((attempt): attempt is UploadCheckpoint => attempt !== undefined);
    if (attempts.length === 0) return;

    attempts.forEach((attempt) => currentAttempts.delete(attempt.attemptId));
    this.saveAttempts(currentAttempts);
    const latest = attempts.reduce((current, attempt) => (
      attempt.updatedAt >= current.updatedAt ? attempt : current
    ));
    trackAnalyticsEvent('image_upload_interrupted', {
      interrupted_count: attempts.length,
      latest_stage: latest.stage,
      provider: latest.provider,
      checkpoint_age_bucket: this.getAgeBucket(Date.now() - latest.updatedAt),
      ...this.toAnalyticsMetrics(latest),
    });
    this.glitchTip.captureMessage(
      'Interrupted image upload detected after restart',
      'warning',
      {
        tags: {
          error_type: 'image_upload_interrupted',
          inferred: 'true',
          latest_stage: latest.stage,
          provider: latest.provider,
        },
        fingerprint: ['image-upload-interrupted'],
        extra: {
          interruptedCount: attempts.length,
          attempts: attempts.map((attempt) => ({
            attemptId: attempt.attemptId,
            stage: attempt.stage,
            provider: attempt.provider,
            ageMs: Date.now() - attempt.updatedAt,
            durationMs: attempt.updatedAt - attempt.startedAt,
            ...this.toAnalyticsMetrics(attempt),
          })),
        },
      },
    );
  }

  private readonly handleChannelMessage = (
    event: MessageEvent<UploadCheckpointChannelMessage>,
  ): void => {
    const message = event.data;
    if (message?.type !== 'probe') return;

    const attemptIds = [...this.loadAttempts().values()]
      .filter((attempt) => attempt.ownerId === this.ownerId)
      .map((attempt) => attempt.attemptId);
    this.channel?.postMessage({
      type: 'active_attempts',
      requestId: message.requestId,
      attemptIds,
    } satisfies UploadCheckpointChannelMessage);
  };

  private findLiveAttemptIds(attempts: Map<string, UploadCheckpoint>): Promise<Set<string>> {
    const liveAttemptIds = new Set(
      [...attempts.values()]
        .filter((attempt) => attempt.ownerId === this.ownerId)
        .map((attempt) => attempt.attemptId),
    );
    if (!this.channel) return Promise.resolve(liveAttemptIds);

    const requestId = crypto.randomUUID();
    return new Promise((resolve) => {
      const handleResponse = (event: MessageEvent<UploadCheckpointChannelMessage>): void => {
        const message = event.data;
        if (message?.type !== 'active_attempts' || message.requestId !== requestId) return;
        message.attemptIds.forEach((attemptId) => liveAttemptIds.add(attemptId));
      };
      this.channel?.addEventListener('message', handleResponse);
      this.channel?.postMessage({ type: 'probe', requestId } satisfies UploadCheckpointChannelMessage);
      setTimeout(() => {
        this.channel?.removeEventListener('message', handleResponse);
        resolve(liveAttemptIds);
      }, LIVE_PROBE_DURATION_MS);
    });
  }

  private collectResourceMetrics(): UploadResourceMetrics {
    const heap = typeof performance === 'undefined'
      ? undefined
      : (performance as PerformanceWithMemory).memory;
    const deviceMemory = typeof navigator === 'undefined'
      ? undefined
      : (navigator as NavigatorWithDeviceMemory).deviceMemory;

    return {
      ...(heap?.usedJSHeapSize !== undefined ? { heapUsedBytes: heap.usedJSHeapSize } : {}),
      ...(heap?.totalJSHeapSize !== undefined ? { heapTotalBytes: heap.totalJSHeapSize } : {}),
      ...(heap?.jsHeapSizeLimit !== undefined ? { heapLimitBytes: heap.jsHeapSizeLimit } : {}),
      ...(deviceMemory !== undefined ? { deviceMemoryGb: deviceMemory } : {}),
    };
  }

  private addStageBreadcrumb(checkpoint: UploadCheckpoint): void {
    const metrics = this.toAnalyticsMetrics(checkpoint);
    this.glitchTip.addBreadcrumb({
      category: 'image_upload_checkpoint',
      message: checkpoint.stage,
      level: 'info',
      data: {
        attemptId: checkpoint.attemptId,
        provider: checkpoint.provider,
        durationMs: checkpoint.updatedAt - checkpoint.startedAt,
        ...metrics,
      },
    });
  }

  private toAnalyticsMetrics(metrics: UploadResourceMetrics): Record<string, number> {
    return {
      ...(metrics.encodedSizeBytes !== undefined
        ? { encoded_size_bytes: metrics.encodedSizeBytes }
        : {}),
      ...(metrics.fileSizeBytes !== undefined ? { file_size_bytes: metrics.fileSizeBytes } : {}),
      ...(metrics.heapUsedBytes !== undefined ? { heap_used_bytes: metrics.heapUsedBytes } : {}),
      ...(metrics.heapTotalBytes !== undefined ? { heap_total_bytes: metrics.heapTotalBytes } : {}),
      ...(metrics.heapLimitBytes !== undefined ? { heap_limit_bytes: metrics.heapLimitBytes } : {}),
      ...(metrics.deviceMemoryGb !== undefined ? { device_memory_gb: metrics.deviceMemoryGb } : {}),
    };
  }

  private getAgeBucket(ageMs: number): string {
    if (ageMs < 60_000) return 'under_1m';
    if (ageMs < 5 * 60_000) return '1m_to_5m';
    if (ageMs < 60 * 60_000) return '5m_to_1h';
    return 'over_1h';
  }

  private loadAttempts(): Map<string, UploadCheckpoint> {
    try {
      const serialized = localStorage.getItem(STORAGE_KEY);
      if (!serialized) return new Map();

      const registry = JSON.parse(serialized) as UploadCheckpointRegistry;
      if (registry.version !== 1 || !registry.attempts || typeof registry.attempts !== 'object') {
        localStorage.removeItem(STORAGE_KEY);
        return new Map();
      }

      return new Map(Object.entries(registry.attempts));
    } catch {
      return new Map();
    }
  }

  private saveAttempts(attempts: Map<string, UploadCheckpoint>): void {
    try {
      if (attempts.size === 0) {
        localStorage.removeItem(STORAGE_KEY);
        return;
      }

      localStorage.setItem(STORAGE_KEY, JSON.stringify({
        version: 1,
        attempts: Object.fromEntries(attempts),
      } satisfies UploadCheckpointRegistry));
    } catch {
      return;
    }
  }
}
